import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Typography, Button, Space, Tag, Alert, Skeleton, Input, Select, Pagination, Card, Drawer, Collapse, InputNumber, Empty, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { 
    SearchOutlined, 
    FilterOutlined, 
    ReloadOutlined
} from '@ant-design/icons';
import auctionService from '../services/auctionService';
import CompanyDrawer from '../components/Common/CompanyDrawer';
import FeaturedLots from '../components/Auction/FeaturedLots';
import BrowseLotCard from '../components/Auction/BrowseLotCard';
import BrowseLotCardSkeleton from '../components/Auction/BrowseLotCardSkeleton';
import './BrowseLots.css';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { Panel } = Collapse;

// Helper function to format price
const formatPrice = (price) => {
    if (price === null || price === undefined) return '0.00';
    return Number(price).toFixed(2);
};

const formatCurrency = (price) => {
    return `PKR ${formatPrice(price)}`;
};

const BrowseLots = () => {
    const [lots, setLots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    // Sort options - local state that only applies on search
    const [localSortBy, setLocalSortBy] = useState('created_at');
    const [localSortDirection, setLocalSortDirection] = useState('desc');
    
    // Active sort (applied to search)
    const [sortBy, setSortBy] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 12,
        total: 0
    });
    const [filtersVisible, setFiltersVisible] = useState(false);
    const [companyDrawerVisible, setCompanyDrawerVisible] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const resultsRef = useRef(null);
    const shouldScrollRef = useRef(false);
    
    // Advanced filters - local state that only applies on search
    const [localFilters, setLocalFilters] = useState({
        status: 'all',
        auction_status: 'all',
        category_id: 'all',
        company_id: null,
        min_price: null,
        max_price: null,
        min_current_price: null,
        max_current_price: null,
        min_quantity: null,
        max_quantity: null,
        has_bids: 'all',
        has_reserve: 'all'
    });
    
    // Active filters (applied to search)
    const [filters, setFilters] = useState({
        status: 'all',
        auction_status: 'all',
        category_id: 'all',
        company_id: null,
        min_price: null,
        max_price: null,
        min_current_price: null,
        max_current_price: null,
        min_quantity: null,
        max_quantity: null,
        has_bids: 'all',
        has_reserve: 'all'
    });

    const navigate = useNavigate();

    // Load categories on mount
    useEffect(() => {
        const loadCategories = async () => {
            try {
                setLoadingCategories(true);
                const response = await auctionService.getCategories();
                setCategories(response.data || []);
            } catch (error) {
                console.error('Error loading categories:', error);
            } finally {
                setLoadingCategories(false);
            }
        };
        loadCategories();
    }, []);

    useEffect(() => {
        fetchLots();
    }, [searchQuery, sortBy, sortDirection, pagination.current, pagination.pageSize, filters]);

    // Scroll to results after loading completes (only when search/filter was triggered)
    useEffect(() => {
        if (!loading && resultsRef.current && shouldScrollRef.current) {
            setTimeout(() => {
                resultsRef.current?.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                });
                shouldScrollRef.current = false; // Reset flag after scrolling
            }, 100);
        }
    }, [loading, lots]);

    const fetchLots = async () => {
        try {
            setLoading(true);
            const params = {
                search: searchQuery,
                sort_by: sortBy,
                sort_direction: sortDirection,
                page: pagination.current,
                per_page: pagination.pageSize,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, value]) => value !== 'all' && value !== null && value !== '')
                )
            };

            const response = await auctionService.browseLots(params);
            
            setLots(response.data);
            
            if (response.meta) {
                setPagination(prev => ({
                    ...prev,
                    current: response.meta.current_page || prev.current,
                    total: response.meta.total || 0,
                    pageSize: response.meta.per_page || prev.pageSize
                }));
            }
            
            setError(null);
        } catch (error) {
            console.error('Error fetching lots:', error);
            setError('Failed to load lots. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        // Close drawer when searching
        setFiltersVisible(false);
        // Set flag to scroll to results after loading
        shouldScrollRef.current = true;
        // Apply local filters and sort to active filters/sort when searching
        setFilters(localFilters);
        setSortBy(localSortBy);
        setSortDirection(localSortDirection);
        setSearchQuery(searchInput);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleFilterChange = (key, value) => {
        // Only update local filters, don't trigger search
        setLocalFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            status: 'all',
            auction_status: 'all',
            category_id: 'all',
            company_id: null,
            min_price: null,
            max_price: null,
            min_current_price: null,
            max_current_price: null,
            min_quantity: null,
            max_quantity: null,
            has_bids: 'all',
            has_reserve: 'all'
        };
        setLocalFilters(clearedFilters);
        setFilters(clearedFilters);
        setLocalSortBy('created_at');
        setLocalSortDirection('desc');
        setSortBy('created_at');
        setSortDirection('desc');
        setSearchQuery('');
        setSearchInput('');
        setPagination(prev => ({ ...prev, current: 1 }));
    };
    
    const handleApplyFilters = () => {
        // Apply filters and close drawer
        handleSearch();
    };

    const handlePaginationChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize
        }));
    };

    const handleViewCompany = (company) => {
        setSelectedCompany(company);
        setCompanyDrawerVisible(true);
    };

    const getDisplayStatus = (status) => {
        if (status?.toLowerCase() === 'closed') {
            return 'sold';
        }
        return status;
    };

    const removeFilter = (key) => {
        const newFilters = { ...filters };
        if (key === 'search') {
            setSearchQuery('');
            setSearchInput('');
        } else if (key === 'sort') {
            setSortBy('created_at');
            setSortDirection('desc');
            setLocalSortBy('created_at');
            setLocalSortDirection('desc');
        } else {
            const defaultValue = key.includes('price') || key.includes('quantity') ? null : 'all';
            newFilters[key] = defaultValue;
            setFilters(newFilters);
            setLocalFilters({ ...newFilters });
        }
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const renderActiveFilters = () => {
        const activeFilters = [];
        
        // Search query
        if (searchQuery) {
            activeFilters.push({
                key: 'search',
                label: `Search: "${searchQuery}"`,
                value: searchQuery
            });
        }

        // Sort
        if (sortBy !== 'created_at' || sortDirection !== 'desc') {
            const sortLabel = sortBy === 'created_at' ? 'Date' : 
                            sortBy === 'starting_price' ? 'Price' :
                            sortBy === 'title' ? 'Title' :
                            sortBy === 'bids_count' ? 'Bids' : sortBy;
            activeFilters.push({
                key: 'sort',
                label: `Sort: ${sortLabel} (${sortDirection === 'asc' ? 'Asc' : 'Desc'})`,
                value: `${sortBy}_${sortDirection}`
            });
        }

        // Lot Status
        if (filters.status && filters.status !== 'all') {
            const displayStatus = getDisplayStatus(filters.status);
            activeFilters.push({
                key: 'status',
                label: `Lot Status: ${displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}`,
                value: filters.status
            });
        }

        // Auction Status
        if (filters.auction_status && filters.auction_status !== 'all') {
            activeFilters.push({
                key: 'auction_status',
                label: `Auction Status: ${filters.auction_status.charAt(0).toUpperCase() + filters.auction_status.slice(1)}`,
                value: filters.auction_status
            });
        }

        // Category
        if (filters.category_id && filters.category_id !== 'all') {
            const selectedCategory = categories.find(cat => cat.id === parseInt(filters.category_id));
            if (selectedCategory) {
                activeFilters.push({
                    key: 'category_id',
                    label: `Category: ${selectedCategory.name}`,
                    value: filters.category_id
                });
            }
        }

        // Price filters
        if (filters.min_price) {
            activeFilters.push({
                key: 'min_price',
                label: `Min Price: ${formatCurrency(filters.min_price)}`,
                value: filters.min_price
            });
        }
        if (filters.max_price) {
            activeFilters.push({
                key: 'max_price',
                label: `Max Price: ${formatCurrency(filters.max_price)}`,
                value: filters.max_price
            });
        }
        if (filters.min_current_price) {
            activeFilters.push({
                key: 'min_current_price',
                label: `Min Current Price: ${formatCurrency(filters.min_current_price)}`,
                value: filters.min_current_price
            });
        }
        if (filters.max_current_price) {
            activeFilters.push({
                key: 'max_current_price',
                label: `Max Current Price: ${formatCurrency(filters.max_current_price)}`,
                value: filters.max_current_price
            });
        }

        // Quantity filters
        if (filters.min_quantity) {
            activeFilters.push({
                key: 'min_quantity',
                label: `Min Quantity: ${filters.min_quantity}`,
                value: filters.min_quantity
            });
        }
        if (filters.max_quantity) {
            activeFilters.push({
                key: 'max_quantity',
                label: `Max Quantity: ${filters.max_quantity}`,
                value: filters.max_quantity
            });
        }

        // Has bids
        if (filters.has_bids && filters.has_bids !== 'all') {
            activeFilters.push({
                key: 'has_bids',
                label: `Has Bids: ${filters.has_bids === 'yes' ? 'Yes' : 'No'}`,
                value: filters.has_bids
            });
        }

        // Has reserve
        if (filters.has_reserve && filters.has_reserve !== 'all') {
            activeFilters.push({
                key: 'has_reserve',
                label: `Reserve Price: ${filters.has_reserve === 'yes' ? 'Yes' : 'No'}`,
                value: filters.has_reserve
            });
        }

        if (activeFilters.length === 0) return null;

        return (
            <div style={{
                marginBottom: '20px',
                padding: '12px 16px',
                background: '#f5f7fa',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px'
                }}>
                    <Text strong style={{ marginRight: '8px', fontSize: '13px', color: '#595959' }}>
                        Active Filters:
                    </Text>
                    {activeFilters.map(filter => (
                        <Tag
                            key={filter.key}
                            closable
                            onClose={() => removeFilter(filter.key)}
                            style={{
                                margin: 0,
                                padding: '4px 12px',
                                borderRadius: '16px',
                                fontSize: '12px',
                                fontWeight: 500,
                                background: '#ffffff',
                                border: '1px solid #d9d9d9',
                                color: '#595959'
                            }}
                        >
                            {filter.label}
                        </Tag>
                    ))}
                    {activeFilters.length > 1 && (
                        <Button
                            type="link"
                            size="small"
                            onClick={handleClearFilters}
                            style={{
                                padding: '0 8px',
                                height: 'auto',
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#1890ff'
                            }}
                        >
                            Clear All
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    const renderLotCard = (lot) => (
        <BrowseLotCard key={lot.id} lot={lot} onViewCompany={handleViewCompany} />
    );

    const renderSkeleton = () => (
        <Row gutter={[32, 32]}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item) => (
                <Col xs={24} sm={12} md={8} lg={8} key={item}>
                    <BrowseLotCardSkeleton />
                </Col>
            ))}
        </Row>
    );

    return (
        <div className="browse-lots-page">
            {/* Header Section */}
            <div className="browse-lots-hero browse-lots-header" style={{ 
                marginBottom: 40,
                padding: '40px',
                backgroundImage: "url(/assets/images/lot-image.jpg)",
                backgroundSize: 'cover',
                backgroundPosition: 'center 100%',
                backgroundRepeat: 'no-repeat',
                boxShadow: '0 20px 60px rgba(24, 144, 255, 0.2)',
                position: 'relative',
                overflow: 'hidden',
                margin: '0 0 40px 0',
                minHeight: '400px'
            }}>
                {/* Enhanced background decoration */}
                <div style={{
                    position: 'absolute',
                    top: -80,
                    right: -80,
                    width: 300,
                    height: 300,
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '50%',
                    zIndex: 1,
                    animation: 'float 6s ease-in-out infinite'
                }} />
                <div style={{
                    position: 'absolute',
                    bottom: -50,
                    left: -50,
                    width: 200,
                    height: 200,
                    background: 'rgba(255, 255, 255, 0.08)',
                    borderRadius: '50%',
                    zIndex: 1,
                    animation: 'float 8s ease-in-out infinite reverse'
                }} />
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 100,
                    height: 100,
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '50%',
                    zIndex: 1,
                    animation: 'pulse 4s ease-in-out infinite'
                }} />

                <div style={{ position: 'relative', zIndex: 2 }}>
                    <Row justify="space-between" align="middle" gutter={[24, 24]} className="browse-lots-header-row">
                        <Col>
                            <Title level={1} className="browse-lots-title" style={{ 
                                margin: 0, 
                                color: 'white',
                                fontWeight: 800,
                                fontSize: '42px',
                                lineHeight: 1.2,
                                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                                Browse Lots
                            </Title>
                            <Text className="browse-lots-subtitle" style={{ 
                                fontSize: '20px',
                                color: 'rgba(255, 255, 255, 0.95)',
                                fontWeight: 400,
                                display: 'block',
                                marginTop: '12px',
                                lineHeight: 1.5
                            }}>
                                Discover lots from all active auctions with company details
                            </Text>
                        </Col>
                    </Row>

                    <div className="browse-lots-search-container" style={{ 
                        marginTop: 40,
                        background: 'rgba(255, 255, 255, 0.98)',
                        borderRadius: '16px',
                        padding: '32px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        {/* Search Input Area */}
                        <Row gutter={[20, 20]} align="middle">
                            <Col xs={24}>
                                <div className="search-container">
                                    <Input
                                        placeholder="Search lots by title, description, auction, or category..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        onPressEnter={handleSearch}
                                        allowClear
                                        size="large"
                                        className="search-input"
                                    />
                                    <Button
                                        type="primary"
                                        icon={<SearchOutlined />}
                                        onClick={handleSearch}
                                        className="search-button"
                                        size="large"
                                    >
                                        <span className="search-button-text">Search</span>
                                    </Button>
                                </div>
                            </Col>
                        </Row>

                        {/* Advanced Filters Button */}
                        <Row gutter={[20, 20]} align="middle" style={{ marginTop: 10 }}>
                            <Col xs={24}>
                                <Button
                                    type="default"
                                    size="large"
                                    onClick={() => {
                                        // Sync local filters with active filters when opening drawer
                                        setLocalFilters(filters);
                                        setLocalSortBy(sortBy);
                                        setLocalSortDirection(sortDirection);
                                        setFiltersVisible(true);
                                    }}
                                    className="advanced-filters-button"
                                    block
                                >
                                    <span className="advanced-filters-button-content">
                                        <FilterOutlined style={{ fontSize: '18px' }} />
                                        <span>Advanced Filters</span>
                                    </span>
                                </Button>
                            </Col>
                        </Row>
                    </div>
                </div>

                <style jsx>{`
                    @keyframes float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-20px); }
                    }
                    @keyframes pulse {
                        0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
                        50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.1); }
                    }

                    /* Search Container Styles */
                    .search-container {
                        display: flex;
                        align-items: center;
                        width: 100%;
                    }

                    .search-input {
                        flex: 1;
                        border-radius: 12px 0 0 12px !important;
                        border: 2px solid #e8eaed !important;
                        border-right: none !important;
                        font-size: 16px !important;
                        height: 56px !important;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.04) !important;
                    }

                    .search-input:focus,
                    .search-input:hover {
                        border-color: #1890ff !important;
                        box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1) !important;
                    }

                    .search-button {
                        height: 56px !important;
                        border-radius: 0 12px 12px 0 !important;
                        background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%) !important;
                        border: 2px solid #e8eaed !important;
                        border-left: none !important;
                        font-weight: 600 !important;
                        font-size: 16px !important;
                        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3) !important;
                        padding: 0 28px !important;
                        transition: all 0.3s ease !important;
                    }

                    .search-button:hover {
                        background: linear-gradient(135deg, #40a9ff 0%, #1890ff 100%) !important;
                        transform: translateY(-1px) !important;
                        box-shadow: 0 6px 16px rgba(24, 144, 255, 0.4) !important;
                    }

                    .search-button-text {
                        display: inline;
                    }

                    /* Mobile Responsive Styles */
                    @media (max-width: 768px) {
                        .browse-lots-header {
                            padding: 20px 16px !important;
                            margin: 0 0 24px 0 !important;
                            min-height: 280px !important;
                        }
                        
                        .browse-lots-title {
                            font-size: 28px !important;
                            line-height: 1.3 !important;
                        }
                        
                        .browse-lots-subtitle {
                            font-size: 18px !important;
                            margin-top: 8px !important;
                        }
                        
                        .browse-lots-search-container {
                            margin-top: 24px !important;
                            padding: 20px 16px !important;
                            border-radius: 12px !important;
                        }
                        
                        .browse-lots-header-row {
                            margin: 0 !important;
                        }
                        
                        .browse-lots-header-row .ant-col {
                            padding: 0 !important;
                        }
                        
                        .browse-lots-search-container .ant-row {
                            margin: 0 !important;
                        }
                        
                        .browse-lots-search-container .ant-col {
                            padding: 0 !important;
                        }
                        
                        .search-container {
                            flex-direction: row;
                            gap: 0;
                        }
                        
                        .search-input {
                            border-radius: 12px 0 0 12px !important;
                            border-right: none !important;
                            width: calc(100% - 60px) !important;
                            height: 56px !important;
                            font-size: 16px !important;
                            padding: 0 16px !important;
                        }
                        
                        .search-button {
                            border-radius: 0 12px 12px 0 !important;
                            border-left: none !important;
                            width: 60px !important;
                            height: 56px !important;
                            padding: 0 !important;
                            min-width: 60px !important;
                        }
                        
                        .search-button-text {
                            display: none !important;
                        }
                        
                        .search-button .anticon {
                            font-size: 18px !important;
                        }
                    }

                    @media (max-width: 480px) {
                        .browse-lots-header {
                            padding: 16px 12px !important;
                            margin: 0 0 20px 0 !important;
                            min-height: 240px !important;
                        }
                        
                        .browse-lots-title {
                            font-size: 24px !important;
                            line-height: 1.3 !important;
                        }
                        
                        .browse-lots-subtitle {
                            font-size: 18px !important;
                            margin-top: 6px !important;
                        }
                        
                        .browse-lots-search-container {
                            margin-top: 20px !important;
                            padding: 16px 12px !important;
                            border-radius: 10px !important;
                        }
                        
                        .search-input {
                            height: 50px !important;
                            font-size: 14px !important;
                            padding: 0 12px !important;
                        }
                        
                        .search-button {
                            height: 50px !important;
                            width: 56px !important;
                            min-width: 56px !important;
                        }
                        
                        .search-input {
                            width: calc(100% - 56px) !important;
                        }
                        
                        .search-button .anticon {
                            font-size: 16px !important;
                        }
                    }

                    /* Advanced Filters Button Styles */
                    .advanced-filters-button {
                        height: 56px !important;
                        border-radius: 12px !important;
                        background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
                        border: 2px solid #e8eaed !important;
                        font-weight: 600 !important;
                        font-size: 16px !important;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
                        position: relative !important;
                        overflow: hidden !important;
                        margin-top: 10px;
                    }

                    .advanced-filters-button::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: -100%;
                        width: 100%;
                        height: 100%;
                        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
                        transition: left 0.5s;
                    }

                    .advanced-filters-button:hover::before {
                        left: 100%;
                    }

                    .advanced-filters-button:hover {
                        background: linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%) !important;
                        border-color: #1890ff !important;
                        box-shadow: 0 4px 16px rgba(24, 144, 255, 0.2) !important;
                        transform: translateY(-2px) !important;
                    }

                    .advanced-filters-button:active {
                        transform: translateY(0px) !important;
                        box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
                    }

                    .advanced-filters-button-content {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                        color: #1890ff;
                        font-weight: 600;
                        letter-spacing: 0.3px;
                    }

                    .advanced-filters-button:hover .advanced-filters-button-content {
                        color: #096dd9;
                    }

                    .advanced-filters-button .anticon {
                        transition: transform 0.3s ease;
                    }

                    .advanced-filters-button:hover .anticon {
                        transform: rotate(15deg) scale(1.1);
                    }

                    @media (max-width: 768px) {
                        .advanced-filters-button {
                            height: 48px !important;
                            font-size: 14px !important;
                        }
                        
                        .advanced-filters-button-content {
                            gap: 6px !important;
                        }
                        
                        .advanced-filters-button-content .anticon {
                            font-size: 16px !important;
                        }
                    }

                    @media (max-width: 480px) {
                        .advanced-filters-button {
                            height: 44px !important;
                            font-size: 13px !important;
                        }
                        
                        .advanced-filters-button-content {
                            gap: 5px !important;
                        }
                        
                        .advanced-filters-button-content .anticon {
                            font-size: 14px !important;
                        }
                    }

                    /* Drawer Footer Buttons Styles */
                    .drawer-footer-buttons {
                        display: flex;
                        gap: 16px;
                        padding: 8px 0;
                        border-top: 1px solid #f0f0f0;
                        margin-top: 16px;
                    }

                    .drawer-cancel-button,
                    .drawer-apply-button {
                        flex: 1;
                        height: 48px !important;
                        border-radius: 10px !important;
                        font-weight: 600 !important;
                        font-size: 16px !important;
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
                    }

                    .drawer-cancel-button {
                        background: #ffffff !important;
                        border: 2px solid #d9d9d9 !important;
                        color: #595959 !important;
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06) !important;
                    }

                    .drawer-cancel-button:hover {
                        background: #fafafa !important;
                        border-color: #40a9ff !important;
                        color: #1890ff !important;
                        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15) !important;
                        transform: translateY(-1px) !important;
                    }

                    .drawer-cancel-button:active {
                        transform: translateY(0px) !important;
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06) !important;
                    }

                    .drawer-apply-button {
                        background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%) !important;
                        border: none !important;
                        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3) !important;
                    }

                    .drawer-apply-button:hover {
                        background: linear-gradient(135deg, #40a9ff 0%, #1890ff 100%) !important;
                        box-shadow: 0 6px 16px rgba(24, 144, 255, 0.4) !important;
                        transform: translateY(-2px) !important;
                    }

                    .drawer-apply-button:active {
                        transform: translateY(0px) !important;
                        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3) !important;
                    }

                    .drawer-apply-button .anticon {
                        font-size: 18px !important;
                        margin-right: 8px;
                    }

                    @media (max-width: 768px) {
                        .drawer-footer-buttons {
                            gap: 12px;
                        }

                        .drawer-cancel-button,
                        .drawer-apply-button {
                            height: 44px !important;
                            font-size: 15px !important;
                        }
                    }

                    @media (max-width: 480px) {
                        .drawer-footer-buttons {
                            gap: 10px;
                            flex-direction: column;
                        }

                        .drawer-cancel-button,
                        .drawer-apply-button {
                            height: 46px !important;
                            font-size: 16px !important;
                            width: 100% !important;
                        }
                    }
                `}</style>
            </div>

            {/* Featured Lots Section */}
            <FeaturedLots limit={10} showViewAll={true} />

            {/* Active Filters */}
            {renderActiveFilters()}

            {/* Searching Overlay */}
            {loading && (searchQuery || Object.values(filters).some(value => value !== 'all' && value !== null && value !== '')) && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '20px'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '32px 48px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '20px',
                        minWidth: '300px',
                        maxWidth: '400px'
                    }}>
                        <Spin size="large" />
                        <Text strong style={{ 
                            fontSize: '18px', 
                            color: '#262626',
                            margin: 0,
                            textAlign: 'center'
                        }}>
                            Please wait, searching for results...
                        </Text>
                    </div>
                </div>
            )}

            {/* Results */}
            <div className="lots-grid" ref={resultsRef}>
                {loading ? (
                    renderSkeleton()
                ) : error ? (
                    <div className="empty-container">
                        <div className="empty-card">
                            <Alert
                                message="Error"
                                description={error}
                                type="error"
                                showIcon
                            />
                        </div>
                    </div>
                ) : lots.length === 0 ? (
                    <div className="empty-container">
                        <div className="empty-card">
                            <div style={{ 
                                fontSize: '64px', 
                                marginBottom: '24px',
                                opacity: 0.3
                            }}>
                                📦
                            </div>
                            <Text className="empty-title">
                                No lots found
                            </Text>
                            <Text className="empty-description">
                                No lots are currently available. Check back later for new lots or try adjusting your filters.
                            </Text>
                            <div style={{ marginTop: '24px' }}>
                                <Button 
                                    type="primary" 
                                    onClick={handleClearFilters}
                                    style={{
                                        borderRadius: '8px',
                                        height: '40px',
                                        padding: '0 24px',
                                        fontWeight: 500
                                    }}
                                >
                                    Clear All Filters
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <Row gutter={[32, 32]}>
                            {lots.map(lot => (
                                <Col xs={24} sm={12} md={8} lg={8} key={lot.id}>
                                    {renderLotCard(lot)}
                                </Col>
                            ))}
                        </Row>
                        
                        {pagination.total > 0 && (
                            <div style={{ 
                                marginTop: 32,
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '24px 0'
                            }}>
                                <Pagination
                                    current={pagination.current}
                                    pageSize={pagination.pageSize}
                                    total={pagination.total}
                                    onChange={handlePaginationChange}
                                    showSizeChanger={false}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Advanced Filters Drawer */}
            <Drawer
                title={
                    <Space>
                        <FilterOutlined />
                        <span>Advanced Filters</span>
                    </Space>
                }
                placement="right"
                onClose={() => setFiltersVisible(false)}
                open={filtersVisible}
                width={400}
                extra={
                    <Space>
                        <Button
                            type="link"
                            icon={<ReloadOutlined />}
                            onClick={handleClearFilters}
                        >
                            Clear All
                        </Button>
                    </Space>
                }
                footer={
                    <div className="drawer-footer-buttons">
                        <Button 
                            onClick={() => {
                                // Reset local filters to match active filters (don't close drawer)
                                setLocalFilters({ ...filters });
                            }}
                            className="drawer-cancel-button"
                            size="large"
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            icon={<SearchOutlined />}
                            onClick={handleApplyFilters}
                            className="drawer-apply-button"
                            size="large"
                        >
                            Apply & Search
                        </Button>
                    </div>
                }
            >
                <Collapse defaultActiveKey={['1', '2', '3', '4']} ghost>
                    <Panel header="Sort Options" key="1">
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            <div>
                                <Text strong>Sort By</Text>
                                <Select
                                    value={`${localSortBy}-${localSortDirection}`}
                                    onChange={(value) => {
                                        const [newSortBy, newSortDirection] = value.split('-');
                                        setLocalSortBy(newSortBy);
                                        setLocalSortDirection(newSortDirection);
                                    }}
                                    style={{ width: '100%', marginTop: 8 }}
                                >
                                    <Option value="created_at-desc">Newest First</Option>
                                    <Option value="created_at-asc">Oldest First</Option>
                                    <Option value="title-asc">Title A-Z</Option>
                                    <Option value="title-desc">Title Z-A</Option>
                                    <Option value="starting_price-asc">Price: Low to High</Option>
                                    <Option value="starting_price-desc">Price: High to Low</Option>
                                    <Option value="current_price-asc">Current Price: Low to High</Option>
                                    <Option value="current_price-desc">Current Price: High to Low</Option>
                                    <Option value="bids_count-desc">Most Bids</Option>
                                    <Option value="bids_count-asc">Fewest Bids</Option>
                                </Select>
                            </div>
                        </Space>
                    </Panel>

                    <Panel header="Status Filters" key="2">
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            <div>
                                <Text strong>Category</Text>
                                <Select
                                    value={localFilters.category_id}
                                    onChange={(value) => handleFilterChange('category_id', value)}
                                    style={{ width: '100%', marginTop: 8 }}
                                    loading={loadingCategories}
                                    showSearch
                                    placeholder="Select a category"
                                    filterOption={(input, option) =>
                                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    <Option value="all">All Categories</Option>
                                    {categories.map(category => (
                                        <Option key={category.id} value={category.id}>
                                            {category.name}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            <div>
                                <Text strong>Lot Status</Text>
                                <Select
                                    value={localFilters.status}
                                    onChange={(value) => handleFilterChange('status', value)}
                                    style={{ width: '100%', marginTop: 8 }}
                                >
                                    <Option value="all">All Statuses</Option>
                                    <Option value="active">Active</Option>
                                    <Option value="closed">Sold</Option>
                                    <Option value="cancelled">Cancelled</Option>
                                    <Option value="timeout">Timeout</Option>
                                </Select>
                            </div>
                            <div>
                                <Text strong>Auction Status</Text>
                                <Select
                                    value={localFilters.auction_status}
                                    onChange={(value) => handleFilterChange('auction_status', value)}
                                    style={{ width: '100%', marginTop: 8 }}
                                >
                                    <Option value="all">All Statuses</Option>
                                    <Option value="active">Active</Option>
                                    <Option value="completed">Completed</Option>
                                    <Option value="cancelled">Cancelled</Option>
                                </Select>
                            </div>
                        </Space>
                    </Panel>

                    <Panel header="Price Filters" key="3">
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <div>
                                <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: 12 }}>Starting Price Range</Text>
                                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                                    <InputNumber
                                        placeholder="Minimum Starting Price"
                                        value={localFilters.min_price}
                                        onChange={(value) => handleFilterChange('min_price', value)}
                                        style={{ width: '100%' }}
                                        prefix="PKR"
                                        min={0}
                                        size="large"
                                        controls={true}
                                    />
                                    <InputNumber
                                        placeholder="Maximum Starting Price"
                                        value={localFilters.max_price}
                                        onChange={(value) => handleFilterChange('max_price', value)}
                                        style={{ width: '100%' }}
                                        prefix="PKR"
                                        min={0}
                                        size="large"
                                        controls={true}
                                    />
                                </Space>
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: 12 }}>Current Price Range</Text>
                                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                                    <InputNumber
                                        placeholder="Minimum Current Price"
                                        value={localFilters.min_current_price}
                                        onChange={(value) => handleFilterChange('min_current_price', value)}
                                        style={{ width: '100%' }}
                                        prefix="PKR"
                                        min={0}
                                        size="large"
                                        controls={true}
                                    />
                                    <InputNumber
                                        placeholder="Maximum Current Price"
                                        value={localFilters.max_current_price}
                                        onChange={(value) => handleFilterChange('max_current_price', value)}
                                        style={{ width: '100%' }}
                                        prefix="PKR"
                                        min={0}
                                        size="large"
                                        controls={true}
                                    />
                                </Space>
                            </div>
                        </Space>
                    </Panel>

                    <Panel header="Other Filters" key="4">
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            <div>
                                <Text strong>Quantity Range</Text>
                                <Space style={{ width: '100%', marginTop: 8 }}>
                                    <InputNumber
                                        placeholder="Min"
                                        value={localFilters.min_quantity}
                                        onChange={(value) => handleFilterChange('min_quantity', value)}
                                        style={{ width: '48%' }}
                                        min={1}
                                    />
                                    <InputNumber
                                        placeholder="Max"
                                        value={localFilters.max_quantity}
                                        onChange={(value) => handleFilterChange('max_quantity', value)}
                                        style={{ width: '48%' }}
                                        min={1}
                                    />
                                </Space>
                            </div>
                            <div>
                                <Text strong>Has Bids</Text>
                                <Select
                                    value={localFilters.has_bids}
                                    onChange={(value) => handleFilterChange('has_bids', value)}
                                    style={{ width: '100%', marginTop: 8 }}
                                >
                                    <Option value="all">All</Option>
                                    <Option value="yes">Has Bids</Option>
                                    <Option value="no">No Bids</Option>
                                </Select>
                            </div>
                            <div>
                                <Text strong>Reserve Price</Text>
                                <Select
                                    value={localFilters.has_reserve}
                                    onChange={(value) => handleFilterChange('has_reserve', value)}
                                    style={{ width: '100%', marginTop: 8 }}
                                >
                                    <Option value="all">All</Option>
                                    <Option value="yes">Has Reserve Price</Option>
                                    <Option value="no">No Reserve Price</Option>
                                </Select>
                            </div>
                        </Space>
                    </Panel>
                </Collapse>
            </Drawer>

            {/* Company Drawer */}
            {selectedCompany && (
                <CompanyDrawer
                    visible={companyDrawerVisible}
                    onClose={() => {
                        setCompanyDrawerVisible(false);
                        setSelectedCompany(null);
                    }}
                    companyData={selectedCompany}
                />
            )}
            
            <style jsx>{`
                .hover-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                }
                
                .ant-card:hover .hover-overlay {
                    opacity: 1;
                }

                /* Lot Details Button Animation */
                .lot-details-button {
                    position: relative;
                    overflow: hidden;
                }

                .lot-details-button::before {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    width: 0;
                    height: 0;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.3);
                    transform: translate(-50%, -50%);
                    transition: width 0.6s, height 0.6s;
                }

                .lot-details-button:hover::before {
                    width: 300px;
                    height: 300px;
                }

                .lot-details-button:active {
                    transform: translateY(0) scale(0.98) !important;
                    box-shadow: 0 2px 4px rgba(24, 144, 255, 0.3) !important;
                }

                .lot-details-button .anticon {
                    transition: transform 0.3s ease;
                }

                .lot-details-button:hover .anticon {
                    transform: scale(1.1) rotate(5deg);
                }

                @keyframes pulse {
                    0%, 100% {
                        box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
                    }
                    50% {
                        box-shadow: 0 4px 16px rgba(24, 144, 255, 0.5);
                    }
                }

                .lot-details-button:hover {
                    animation: pulse 2s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
};

export default BrowseLots;

